import { useEffect, useState } from "react";
import { getTimesheets } from "../../services/timesheet";
import Header from "../../components/Header";
import useAuthUser from "../../hooks/useAuthUser";
import TimesheetList from "../../components/timesheet/TimesheetList";
import useLoader from "../../hooks/useLoader";
import TimesheetFilter from "../../components/timesheet/TimesheetFilter";
import Pagination from "../../components/common/Pagination";

const TimesheetPage = () => {
  const { loggedInUser, isAdminHrRole, isManagerRole } = useAuthUser();
  const loggedInUserId = Number(loggedInUser?.id) || null;
  const { hideLoader, showLoader } = useLoader();
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({});
  // eslint-disable-next-line
  const [page, setPage] = useState(1);
  const [activeFilters, setActiveFilters] = useState({});
  const [docPath, setDocPath] = useState("");
  const [defaultReportingManager, setDefaultReportingManager] = useState(null);
  const [reportingManagers, setReportingManagers] = useState([]);

  // fetching timesheet
  const fetchTimesheets = async (pageNo = 1, params = {}) => {
    try {
      showLoader();
      const res = await getTimesheets(pageNo, params);
      if (res.status === 200) {
        setData(res.data?.data?.timesheetData || []);
        setPagination(res.data?.data?.pagination || {});
        setDocPath(res.data.data.docPath || "");
      } else {
        setData([]);
        setPagination({});
      }
    } catch (error) {
      console.log(error);
      setData([]);
      setPagination({});
    } finally {
      hideLoader();
    }
  };

  const filterTimesheets = (params = {}) => {
    setActiveFilters(params);
    fetchTimesheets(1, params);
  };

  useEffect(() => {
    const init = async () => {
      if (isManagerRole || isAdminHrRole) {
        try {
          const defaultFilter = {};
          const res = isManagerRole
            ? await import("../../services/timesheet").then((m) =>
                m.fetchMyReporteeManagers()
              )
            : await import("../../services/exitManagement").then((m) =>
                m.fetchReportingManagers()
              );

          if (res.status === 200) {
            const managers = res.data.data;
            setReportingManagers(managers);
            const self = managers.find((mgr) => mgr._UserID === loggedInUserId);
            if (self) {
              setDefaultReportingManager(self);
              defaultFilter.reporting_manager = self._UserID;
            }
            defaultFilter.user_status = "1";
            setActiveFilters(defaultFilter);
            fetchTimesheets(1, defaultFilter);
            return;
          }
        } catch (e) {
          console.error("Failed to set default reporting manager", e);
        }
      }
      fetchTimesheets();
    };

    init();
    // eslint-disable-next-line
  }, []);
  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchTimesheets(newPage, activeFilters);
  };

  const handleExport = async (exportParams = {}) => {
    try {
      showLoader();
      const response = await getTimesheets(1, exportParams);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "timesheets.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log("error while exporting timesheets", error);
    } finally {
      hideLoader();
    }
  };

  const addButton = {
    allowedRoles: ["employee"],
    link: "/timesheet/add-timesheet",
    text: "Add Timesheet",
    icon: (
      <span className="icon">
        <img
          src="/public/images/icons/icon-addtimesheet.svg"
          alt="add timesheet"
        />
      </span>
    ),
  };

  return (
    <div>
      <Header title="Timesheet" addButton={addButton} />
      <TimesheetFilter
        onFilter={filterTimesheets}
        exportTimesheets={handleExport}
        defaultReportingManager={defaultReportingManager}
        reportingManagers={reportingManagers}
      />
      <TimesheetList
        timesheetData={data}
        onFilter={filterTimesheets}
        activeFilters={activeFilters}
        docPath={docPath}
      />

      {pagination && data.length > 0 && (
        <Pagination
          totalPages={pagination.totalPages}
          currentPage={Number(pagination.pageNo)}
          perPage={Number(pagination.perPage)}
          totalRecords={pagination.totalRecords}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default TimesheetPage;
